import React, { Component } from "react";
import { connect } from "react-redux";
import {
  getTelnyxToken,
  getPhoneCall,
  phoneCall,
  setActiveDialerContact,
  resetActiveCall,
  updateActiveCall,
  setDialerConversation,
  setRejectedCall,
  showErrorMessage,
  playAudio,
  killAudio,
  callControl,
  killAllAudio
} from "app/NativeActions";
import TelnyxDialerInner from "app/DealMachineCore/components/DialerWidget/TelnyxDialerInner";
import { TelnyxRTC } from "@telnyx/webrtc";
import moment from "moment";

class TelnyxDeviceHandler extends Component {
  constructor(props) {
    super(props);

    this.state = {
      incoming_call_id: null,
      incoming_call: null,
      incoming_call_info: null, // Track incoming call information
      loading_call: false,
      loading_active_call: false,
      telnyx_token_request_time: null,
      hide_purchase_upsell: false,
      mute: false,
      reset_connection: false,
      connectionStatus: "disconnected", // 'connecting', 'connected', 'reconnecting', 'failed'
      reconnectAttempts: 0,
      maxReconnectAttempts: 5,
      maxReconnectDuration: 120000, // 2 minutes max for all retry attempts
      reconnectStartTime: null, // Track when reconnection started
      isIntentionalDisconnect: false,
      isReconnecting: false, // Track if we're in a reconnection process (separate from status)
      reconnectTimeoutId: null, // Track timeout for cleanup
      isMounted: true, // Track component mount status
      initialConnectionAttempts: 0, // Track initial connection retry attempts
      maxInitialConnectionAttempts: 3, // Max attempts for initial connection
      isInitialConnection: true // Flag to track if this is the first connection
    };

    this.getIncomingCall = this.getIncomingCall.bind(this);
    this.clearIncomingCall = this.clearIncomingCall.bind(this);
    this.rejectIncomingCall = this.rejectIncomingCall.bind(this);
    this.createCallEvents = this.createCallEvents.bind(this);
    this.refreshTelnyxToken = this.refreshTelnyxToken.bind(this);
    this.hidePurchaseUpsell = this.hidePurchaseUpsell.bind(this);

    this.muteCall = this.muteCall.bind(this);
    this.endCall = this.endCall.bind(this);

    this._localstream = null;
    this._audio = null;

    // Synchronous lock to prevent multiple simultaneous reconnection triggers
    // Using instance property instead of state to avoid async setState race conditions
    this._reconnectionLock = false;
    this._lastReconnectionAttempt = 0;
    this._reconnectionCooldown = 5000; // 5 second minimum between reconnection cycles
    this._errorLogTimestamps = {}; // Track error log timestamps for rate limiting
    this._errorLogRateLimit = 10000; // 10 seconds between same error type logs

    // Lock to prevent concurrent token refresh requests
    this._tokenRefreshLock = false;

    this.startRinging = this.startRinging.bind(this);
    this.resetTelnyxDevice = this.resetTelnyxDevice.bind(this);

    this.handleUnexpectedDisconnect =
      this.handleUnexpectedDisconnect.bind(this);
    this.attemptReconnection = this.attemptReconnection.bind(this);
    this.handleReconnectionFailure = this.handleReconnectionFailure.bind(this);
    this.attemptInitialConnection = this.attemptInitialConnection.bind(this);
    this.checkConnectionAndReconnect = this.checkConnectionAndReconnect.bind(this);
    this.handleCallFailure = this.handleCallFailure.bind(this);
  }

  componentDidMount() {
    // Start initial connection with retry logic
    this.attemptInitialConnection();
    this.getActiveIncomingPhoneCall();
  }

  componentWillUnmount() {
    this.setState({ isIntentionalDisconnect: true, isMounted: false });
    // Clear any pending reconnection timeout
    if (this.state.reconnectTimeoutId) {
      clearTimeout(this.state.reconnectTimeoutId);
    }
    clearTimeout(this._kill_audio_timeout);
    killAudio({ audio: this._audio });
  }

  muteCall(mute, call = null) {
    this.setState(
      {
        mute: mute
      },
      () => {
        if (call) {
          if (this.state.mute) {
            call.muteAudio();
          } else {
            call.unmuteAudio();
          }
        }
      }
    );
  }

  endCall() {
    killAudio({ audio: this._audio });

    if (
      this.props.active_call?.call_id &&
      (this.props.active_call.call_status === "loading" ||
        this.props.active_call.call_status === "connected" ||
        this.props.active_call.call_status === "calling")
    ) {
      this.props.phoneCall({
        token: this.props.token,
        type: "end_call",
        call_id: this.props.active_call?.call_id
      });
    }

    if (
      !!this.props.active_call.call_status &&
      this.props.active_call.call_status !== "loading" &&
      this.props.active_call.call_status !== "call_ended"
    ) {
      playAudio({
        web_file: "/assets/sounds/disconnect.mp3",
        volume: 0.1,
        onPrepared: audio => {
          this._audio = audio;
        },
        onEnd: () => {
          killAllAudio();
        }
      });

      this._localstream = null;
      this.props.updateActiveCall({
        ...this.props.active_call,
        call_status: "call_ended"
      });
    }
  }

  startRinging() {
    if (this.props.active_call?.call_status !== "call_ended") {
      playAudio({
        web_file: "/assets/sounds/ringing.mp3",
        volume: 0.5,
        loop: true,
        onPrepared: audio => {
          this._audio = audio;
        }
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.active_dialer_contact?.answered_on_another_device &&
      this.props.active_dialer_contact?.answered_on_another_device !==
      prevProps.active_dialer_contact?.answered_on_another_device &&
      this.state.incoming_call
    ) {
      try {
        this.state.incoming_call.disconnect();
      } catch (error) {
        console.error("Error in ending a call:", error);
      }
      this.clearIncomingCall();
    }
  }

  hidePurchaseUpsell() {
    this.setState({ hide_purchase_upsell: true });
  }

  resetTelnyxDevice() {
    this.setState(
      {
        reset_connection: true,
        isIntentionalDisconnect: true, // Mark as intentional
        isReconnecting: false // Reset reconnecting flag when manually resetting
      },
      () => {
        if (this._device) {
          // Remove listeners FIRST to prevent socket.close from triggering reconnection loop
          this._device.off("telnyx.ready");
          this._device.off("telnyx.notification");
          this._device.off("telnyx.error");
          this._device.off("telnyx.socket.open");
          this._device.off("telnyx.socket.close");
          // THEN disconnect - event fires but no handler exists
          this._device.disconnect();
        }
      }
    );
  }

  // Rate-limited error logging to prevent flooding the backend
  logErrorWithRateLimit(errorType, errorMessage, additionalData = {}) {
    const now = Date.now();
    const lastLogTime = this._errorLogTimestamps[errorType] || 0;

    // Only log if enough time has passed since last log of this type
    if (now - lastLogTime >= this._errorLogRateLimit) {
      this._errorLogTimestamps[errorType] = now;
      this.props.phoneCall({
        token: this.props.token,
        type: "log_error",
        error_message: errorMessage,
        dialer_provider: "telnyx",
        ...additionalData
      });
      return true;
    }
    return false;
  }

  // Handle unexpected disconnection
  handleUnexpectedDisconnect() {
    // CRITICAL: Use synchronous lock to prevent multiple simultaneous triggers
    // This prevents race conditions from multiple socket events firing at once
    if (this._reconnectionLock) {
      console.log("Reconnection already in progress (lock active), skipping duplicate trigger");
      return;
    }

    // Check cooldown - prevent reconnection spam
    const now = Date.now();
    if (now - this._lastReconnectionAttempt < this._reconnectionCooldown) {
      console.log("Reconnection cooldown active, skipping");
      return;
    }

    // Acquire lock IMMEDIATELY (synchronous)
    this._reconnectionLock = true;
    this._lastReconnectionAttempt = now;

    // End any active call immediately
    if (
      this.props.active_call?.call_status === "connected" ||
      this.props.active_call?.call_status === "answered" ||
      this.props.active_call?.call_status === "calling"
    ) {
      this.endCall();
      this.props.showErrorMessage(
        "Call ended due to connection loss",
        "Connection Lost"
      );
    }

    // Only reconnect if we have an active dialer session
    if (
      this.props.active_dialer_contact &&
      !this.state.isIntentionalDisconnect
    ) {
      this.setState({
        connectionStatus: "reconnecting",
        reconnectAttempts: 0,
        reconnectStartTime: Date.now(),
        isReconnecting: true
      });

      // Notify user with prominent message
      this.props.showErrorMessage(
        "Phone service disconnected. Attempting to reconnect automatically...",
        "Connection Lost"
      );

      // Start reconnection attempts
      this.attemptReconnection();
    } else {
      // Release lock if not reconnecting
      this._reconnectionLock = false;
      this.setState({
        connectionStatus: "disconnected"
      });
    }
  }

  // Attempt to reconnect with exponential backoff
  async attemptReconnection() {
    const {
      reconnectAttempts,
      maxReconnectAttempts,
      isMounted,
      reconnectStartTime,
      maxReconnectDuration
    } = this.state;

    // Don't attempt if component is unmounted
    if (!isMounted) {
      return;
    }

    // Check if we've exceeded the total time limit
    if (
      reconnectStartTime &&
      Date.now() - reconnectStartTime > maxReconnectDuration
    ) {
      this.handleReconnectionFailure();
      return;
    }

    if (reconnectAttempts >= maxReconnectAttempts) {
      this.handleReconnectionFailure();
      return;
    }

    // Check if session is still active
    if (!this.props.active_dialer_contact) {
      // Check if still mounted before setState
      if (this.state.isMounted) {
        this.setState({
          connectionStatus: "disconnected",
          reconnectAttempts: 0,
          reconnectTimeoutId: null,
          isReconnecting: false // Reset the reconnecting flag when session ends
        });
      }
      return;
    }

    this.props.phoneCall({
      token: this.props.token,
      type: "log_info",
      message: `Reconnection attempt ${reconnectAttempts + 1
        }/${maxReconnectAttempts}`,
      dialer_provider: "telnyx"
    });

    try {
      // Clean up old connection
      if (this._device) {
        // Remove listeners FIRST to prevent socket.close from triggering reconnection loop
        this._device.off("telnyx.ready");
        this._device.off("telnyx.notification");
        this._device.off("telnyx.error");
        this._device.off("telnyx.socket.open");
        this._device.off("telnyx.socket.close");
        // THEN disconnect - event fires but no handler exists
        this._device.disconnect();
      }

      // Force token refresh and reconnect
      // IMPORTANT: Keep isReconnecting: true throughout this process
      this.setState(
        {
          reset_connection: true,
          telnyx_token: null,
          isIntentionalDisconnect: false, // Reset flag for reconnection
          // Make sure isReconnecting stays true!
          isReconnecting: true
        },
        () => {
          this.refreshTelnyxToken({
            onSuccess: success => {
              // Check if connection actually succeeded
              if (success !== false) {
                // Don't show notification here - let the socket.open handler do it
                // This prevents duplicate notifications

                // Still log the success for debugging
                this.props.phoneCall({
                  token: this.props.token,
                  type: "log_info",
                  message:
                    "Successfully reconnected to Telnyx after disconnection",
                  dialer_provider: "telnyx"
                });

                // Note: Don't reset isReconnecting here - let socket.open handler do it
                // IMPORTANT: Keep isReconnecting: true so the socket.open handler can detect it
                this.setState({
                  connectionStatus: "connected",
                  reconnectAttempts: 0,
                  reconnectTimeoutId: null,
                  reconnectStartTime: null, // Reset the start time
                  // DO NOT RESET isReconnecting here - let socket.open handler do it
                  // Explicitly preserve it!
                  isReconnecting: this.state.isReconnecting
                });
              } else {
                // Connection failed, schedule retry
                this.scheduleReconnectionRetry();
              }
            },
            onError: error => {
              console.error("Reconnection failed with error:", error);
              this.scheduleReconnectionRetry();
            }
          });
        }
      );
    } catch (error) {
      this.scheduleReconnectionRetry();
    }
  }

  scheduleReconnectionRetry() {
    const { reconnectAttempts, maxReconnectAttempts, isMounted } = this.state;

    // Don't schedule if unmounted
    if (!isMounted) return;

    // Increment attempts only on actual failure
    const newAttempts = reconnectAttempts + 1;

    if (newAttempts >= maxReconnectAttempts) {
      this.handleReconnectionFailure();
      return;
    }

    // Exponential backoff: 2s, 4s, 8s, 16s, 30s max
    const delay = Math.min(Math.pow(2, reconnectAttempts) * 2000, 30000);

    // Use rate-limited logging to prevent flooding
    this.logErrorWithRateLimit(
      "reconnection_retry",
      `Reconnection attempt ${reconnectAttempts + 1} failed, retrying in ${delay}ms`
    );

    // Clear any existing timeout
    if (this.state.reconnectTimeoutId) {
      clearTimeout(this.state.reconnectTimeoutId);
    }

    // Schedule next attempt and save timeout ID
    const timeoutId = setTimeout(() => {
      if (this.state.isMounted) {
        this.attemptReconnection();
      }
    }, delay);

    this.setState({
      reconnectAttempts: newAttempts,
      reconnectTimeoutId: timeoutId
    });
  }

  // Handle complete reconnection failure
  handleReconnectionFailure() {
    // Clear any pending timeout
    if (this.state.reconnectTimeoutId) {
      clearTimeout(this.state.reconnectTimeoutId);
    }

    // Release the reconnection lock
    this._reconnectionLock = false;

    this.setState({
      connectionStatus: "failed",
      reconnectAttempts: 0,
      reconnectTimeoutId: null,
      isReconnecting: false
    });

    this.props.showErrorMessage(
      "Unable to reconnect to phone service. Please refresh the page and try again.",
      "Connection Failed"
    );

    // Log the failure with rate limiting
    this.logErrorWithRateLimit(
      "reconnection_failure",
      `Failed to reconnect after ${this.state.maxReconnectAttempts} attempts`
    );
  }

  // Attempt initial connection with retry logic
  async attemptInitialConnection() {
    const { initialConnectionAttempts, maxInitialConnectionAttempts, isMounted } = this.state;

    // Don't attempt if component is unmounted
    if (!isMounted) {
      return;
    }

    // Check if we've exceeded max attempts
    if (initialConnectionAttempts >= maxInitialConnectionAttempts) {
      // Final failure - notify user
      this.setState({
        connectionStatus: "failed",
        initialConnectionAttempts: 0,
        isInitialConnection: false
      });

      this.props.showErrorMessage(
        "Unable to connect to phone service. Please check your internet connection and refresh the page.",
        "Connection Failed"
      );

      this.props.phoneCall({
        token: this.props.token,
        type: "log_error",
        error_message: `Failed initial connection after ${maxInitialConnectionAttempts} attempts`,
        dialer_provider: "telnyx"
      });
      return;
    }

    // Update attempt count
    this.setState({
      initialConnectionAttempts: initialConnectionAttempts + 1,
      connectionStatus: "connecting"
    });

    // Log the attempt
    if (initialConnectionAttempts > 0) {
      console.log(`Initial connection attempt ${initialConnectionAttempts + 1}/${maxInitialConnectionAttempts}`);

      // Show user feedback for retries
      this.props.showErrorMessage(
        `Retrying connection to phone service (attempt ${initialConnectionAttempts + 1}/${maxInitialConnectionAttempts})...`,
        "Connecting"
      );
    }

    // Attempt connection
    this.refreshTelnyxToken({
      onSuccess: success => {
        if (success !== false) {
          // Success - mark initial connection complete
          this.setState({
            isInitialConnection: false,
            initialConnectionAttempts: 0
          });

          console.log("Initial connection established successfully");
        } else {
          // Failed - schedule retry with exponential backoff
          if (this.state.initialConnectionAttempts < this.state.maxInitialConnectionAttempts) {
            const delay = Math.pow(2, this.state.initialConnectionAttempts) * 1000; // 2s, 4s, 8s

            console.log(`Initial connection failed, retrying in ${delay}ms`);

            setTimeout(() => {
              if (this.state.isMounted && this.state.isInitialConnection) {
                this.attemptInitialConnection();
              }
            }, delay);
          } else {
            // Final attempt failed - do not retry
            console.log("Initial connection failed after all attempts");
          }
        }
      },
      onError: error => {
        console.error("Initial connection failed with error:", error);

        // Schedule retry with exponential backoff
        if (this.state.initialConnectionAttempts < this.state.maxInitialConnectionAttempts) {
          const delay = Math.pow(2, this.state.initialConnectionAttempts) * 1000;

          console.log(`Initial connection error, retrying in ${delay}ms`);

          setTimeout(() => {
            if (this.state.isMounted && this.state.isInitialConnection) {
              this.attemptInitialConnection();
            }
          }, delay);
        } else {
          // Final attempt failed - do not retry
          console.log("Initial connection error - max attempts reached");
        }
      }
    });
  }

  // Check connection status and trigger reconnection if needed
  checkConnectionAndReconnect() {
    // If already reconnecting, don't duplicate efforts
    if (this.state.isReconnecting) {
      console.log("Already reconnecting, skipping duplicate attempt");
      return false;
    }

    // Quick validation - if connected, return true
    if (this._device && this.state.connectionStatus === "connected") {
      // Connection appears to be good
      return true;
    }

    // Connection appears dead - trigger reconnection immediately
    // This uses the existing reconnection logic with exponential backoff
    if (this.state.connectionStatus !== "reconnecting" &&
      this.state.connectionStatus !== "connecting") {
      console.log("Connection check failed, triggering immediate reconnection");
      this.props.phoneCall({
        token: this.props.token,
        type: "log_info",
        message: "Connection check failed - triggering immediate reconnection",
        dialer_provider: "telnyx"
      });
      this.handleUnexpectedDisconnect();
    }

    return false;
  }

  // Handle call failures and detect connection issues
  handleCallFailure(error) {
    // Check if this error indicates a connection problem
    const connectionErrors = [
      "websocket",
      "socket",
      "connection",
      "network",
      "enotfound",
      "etimedout",
      "econnrefused",
      "disconnected"
    ];

    const errorString = (error?.toString() || '').toLowerCase();
    const isConnectionError = connectionErrors.some(term =>
      errorString.includes(term)
    );

    if (isConnectionError) {
      console.log(
        "Call failed due to connection error, triggering immediate reconnection"
      );
      this.props.phoneCall({
        token: this.props.token,
        type: "log_error",
        error_message: `Call failed with connection error: ${error}`,
        dialer_provider: "telnyx"
      });

      // Only trigger if not already reconnecting
      if (!this.state.isReconnecting &&
        this.state.connectionStatus !== "reconnecting") {
        this.handleUnexpectedDisconnect();
      }
    }

    return error; // Return the error for further processing
  }

  refreshTelnyxToken({ onSuccess = null, onError = null }) {
    // Check if token refresh is already in progress
    if (this._tokenRefreshLock) {
      console.log("Token refresh already in progress, waiting for in-flight refresh to complete");
      if (onSuccess) {
        const checkLockAndInvoke = () => {
          if (!this._tokenRefreshLock) {
            onSuccess();
          } else {
            setTimeout(checkLockAndInvoke, 100);
          }
        };
        checkLockAndInvoke();
      }
      return;
    }

    if (
      !this.state.telnyx_token ||
      !this.state.telnyx_token_request_time ||
      moment().diff(moment(this.state.telnyx_token_request_time), "minutes") >
      5 ||
      !this._device ||
      this.state.reset_connection
    ) {
      // Acquire the token refresh lock
      this._tokenRefreshLock = true;
      this.props.getTelnyxToken({
        token: this.props.token,
        force_new:
          !this.state.telnyx_token || !this.state.telnyx_token_request_time,
        onLoading: () => { },
        onError: error => {
          // Release the token refresh lock
          this._tokenRefreshLock = false;

          console.error("Error in getting Telnyx token:", error);

          // Use rate-limited logging to prevent flooding
          this.logErrorWithRateLimit("token_error", `Error generating token: ${error}`, {
            to_phone_number: this.props.active_call?.to_phone_number,
            from_phone_number: this.props.active_call?.from_phone_number
          });

          // Call the onError callback if provided
          if (onError) {
            onError(error);
          }

          // Notify user of the authentication failure based on connection state
          if (this.state.isInitialConnection) {
            // Initial connection - don't trigger reconnection
            this.props.showErrorMessage(
              "Unable to connect to phone service. Please check your credentials.",
              "Connection Error"
            );
            // Don't trigger reconnection for initial connection failures
          } else if (this.state.isReconnecting) {
            // Reconnection attempt - schedule retry
            this.props.showErrorMessage(
              "Unable to reconnect to phone service. Retrying...",
              "Reconnection Error"
            );
            this.scheduleReconnectionRetry();
          } else {
            // Regular operation error
            this.props.showErrorMessage(
              "Unable to authenticate with phone service.",
              "Authentication Error"
            );
          }
        },
        onSuccess: results => {
          // Release the token refresh lock
          this._tokenRefreshLock = false;

          if (!!results?.credential_id) {
            this.props.phoneCall({
              token: this.props.token,
              type: "log_info",
              message: "Telnyx token retrieved successfully.",
              details: results
            });
            this._device = new TelnyxRTC({
              password: results?.password,
              login: results?.user_name,
              ringtoneFile: "/assets/sounds/incoming.mp3"
            });

            this.setState(
              {
                telnyx_token: results?.credential_id,
                telnyx_token_request_time: new Date(),
                connectionStatus: "connecting", // Set status to connecting when we start
                // PRESERVE the isReconnecting flag - don't reset it!
                isReconnecting: this.state.isReconnecting
              },
              () => {
                if (this._device) {
                  this._device.on("telnyx.ready", () => {
                    // Don't override status if we're already connecting or connected
                    if (this.state.connectionStatus === "disconnected") {
                      this.setState({
                        connectionStatus: "connecting"
                      });
                    }
                  });

                  this._device.on("telnyx.error", d => {
                    if (!!d?.error?.message) {
                      // Use rate-limited logging to prevent flooding
                      this.logErrorWithRateLimit("telnyx_error", d?.error?.message, {
                        to_phone_number: this.props.active_call?.to_phone_number,
                        from_phone_number: this.props.active_call?.from_phone_number
                      });
                    }

                    // Check if this is a connection error that requires immediate reconnection
                    // Don't wait for socket.close event
                    const errorMessage = d?.error?.message?.toLowerCase() || "";
                    const isConnectionError =
                      errorMessage.includes("socket") ||
                      errorMessage.includes("connection") ||
                      errorMessage.includes("network") ||
                      errorMessage.includes("websocket") ||
                      errorMessage.includes("disconnected") ||
                      errorMessage.includes("failed to connect");

                    // Only trigger reconnection if not already in progress (check lock)
                    if (
                      isConnectionError &&
                      this.state.connectionStatus === "connected" &&
                      !this.state.isReconnecting &&
                      !this._reconnectionLock
                    ) {
                      console.log(
                        "Telnyx error indicates connection loss, triggering immediate reconnection"
                      );
                      this.handleUnexpectedDisconnect();
                    }
                  });

                  this._device.on("telnyx.socket.open", () => {
                    // Use the isReconnecting flag to determine if this is a reconnection
                    if (this.state.isReconnecting) {
                      // Show success notification to user
                      this.props.showErrorMessage(
                        "Phone service reconnected successfully!",
                        "Connection Restored"
                      );

                      this.props.phoneCall({
                        token: this.props.token,
                        type: "log_info",
                        message: "Telnyx socket reconnection successful",
                        dialer_provider: "telnyx"
                      });
                    } else {
                      // Just log for debugging purposes
                      this.props.phoneCall({
                        token: this.props.token,
                        type: "log_info",
                        message: "Telnyx connection established",
                        dialer_provider: "telnyx"
                      });
                    }

                    // Release reconnection lock on successful connection
                    this._reconnectionLock = false;

                    // Update state after notifications
                    this.setState({
                      connectionStatus: "connected",
                      reconnectAttempts: 0,
                      reconnectTimeoutId: null,
                      reconnectStartTime: null,
                      isIntentionalDisconnect: false,
                      isReconnecting: false,
                      isInitialConnection: false,
                      initialConnectionAttempts: 0
                    });
                  });

                  this._device.on("telnyx.socket.close", () => {
                    console.log("Telnyx socket disconnected.");
                    killAudio({ audio: this._audio });

                    // Only trigger reconnection if:
                    // 1. Not intentional disconnect
                    // 2. Not already reconnecting (lock check)
                    // 3. Not in failed state (prevents infinite retry cycles after max attempts)
                    if (!this.state.isIntentionalDisconnect &&
                        !this._reconnectionLock &&
                        this.state.connectionStatus !== "failed") {
                      this.handleUnexpectedDisconnect();
                    } else if (!this._reconnectionLock) {
                      this.setState({ connectionStatus: "disconnected" });
                    }
                    // If lock is active, another reconnection is in progress - do nothing
                  });

                  this._device.on("telnyx.notification", notification => {
                    const call = notification?.call;

                    if (notification?.type === "callUpdate") {
                      switch (call.state) {
                        case "new":
                          this.muteCall(this.state.mute, call);

                          break;

                        case "early":
                          break;
                        case "trying":
                          if (
                            !this.props.active_dialer_contact.incoming_call &&
                            this.props.active_call?.call_status !==
                            "connected" &&
                            this.props.active_call?.call_status !==
                            "answered" &&
                            this.props.active_call?.call_status !== "call_ended"
                          ) {
                            this.props.updateActiveCall({
                              ...this.props.active_call,
                              call_status: "connected"
                            });
                          }

                          playAudio({
                            web_file: "/assets/sounds/outgoing.mp3",
                            volume: 0.1,
                            onPrepared: audio => {
                              this._audio = audio;
                            },
                            onEnd: this.startRinging
                          });

                          this._localstream = call.localStream;
                          //this._remoteStream = call.remoteStream;

                          break;

                        case "hangup":
                          if (
                            this.props.active_call?.call_status !== "call_ended"
                          ) {
                            this.endCall();
                          }

                          this._kill_audio_timeout = setTimeout(() => {
                            killAllAudio();
                          }, 1000);
                          break;

                        case "destroy":
                          //killAudio({ audio: this._audio });
                          this._kill_audio_timeout = setTimeout(() => {
                            killAllAudio();
                          }, 1000);

                          break;

                        case "active":
                          //if the call is active, we need to make sure we are not muted
                          killAudio({ audio: this._audio });

                          if (
                            this.props.active_call?.call_status !==
                            "answered" &&
                            this.props.active_call?.call_status !== "call_ended"
                          ) {
                            this.props.updateActiveCall({
                              ...this.props.active_call,
                              call_status: "answered"
                            });
                          }

                          this.muteCall(this.state.mute, call);

                          break;
                      }
                    }
                  });

                  this._device.remoteElement = "telnyxRemoteMedia";

                  // Enable microphone with error handling
                  const enableMicrophoneWithRetry = () => {
                    try {
                      this._device.enableMicrophone();
                      return true;
                    } catch (error) {
                      // Handle specific microphone errors
                      if (
                        error.name === "NotReadableError" ||
                        error.message?.includes("Could not start audio source")
                      ) {
                        this.props.showErrorMessage(
                          "Unable to access microphone. Please check:\n• Browser microphone permissions are allowed\n• No other application is using the microphone\n• Try refreshing the page",
                          "Microphone Access Error"
                        );

                        this.props.phoneCall({
                          token: this.props.token,
                          type: "log_error",
                          error_message: `Microphone access failed: ${error.name} - ${error.message}`,
                          dialer_provider: "telnyx"
                        });
                      } else if (
                        error.name === "NotAllowedError" ||
                        error.name === "PermissionDeniedError"
                      ) {
                        this.props.showErrorMessage(
                          "Microphone permission denied. Please allow microphone access in your browser settings and refresh the page.",
                          "Permission Denied"
                        );

                        this.props.phoneCall({
                          token: this.props.token,
                          type: "log_error",
                          error_message: `Microphone permission denied: ${error.name}`,
                          dialer_provider: "telnyx"
                        });
                      } else if (error.name === "NotFoundError") {
                        this.props.showErrorMessage(
                          "No microphone found. Please connect a microphone and refresh the page.",
                          "No Microphone Found"
                        );

                        this.props.phoneCall({
                          token: this.props.token,
                          type: "log_error",
                          error_message: `No microphone found: ${error.name}`,
                          dialer_provider: "telnyx"
                        });
                      } else {
                        // Generic error
                        this.props.showErrorMessage(
                          "Failed to initialize microphone. Please check your audio settings and try again.",
                          "Microphone Error"
                        );

                        this.props.phoneCall({
                          token: this.props.token,
                          type: "log_error",
                          error_message: `Failed to enable microphone: ${error.name} - ${error.message}`,
                          dialer_provider: "telnyx"
                        });
                      }

                      // Return false to indicate failure, but don't throw
                      // This allows the connection to continue even if mic fails
                      return false;
                    }
                  };

                  // Try to enable microphone but don't block connection if it fails
                  const micEnabled = enableMicrophoneWithRetry();
                  if (!micEnabled) {
                    console.warn(
                      "Continuing without microphone - receive-only mode"
                    );
                  }

                  // Single connection attempt (retry logic is handled by attemptInitialConnection or attemptReconnection)
                  const connectToTelnyx = async () => {
                    try {
                      // Log the attempt
                      this.props.phoneCall({
                        token: this.props.token,
                        type: "log_info",
                        message: "Attempting Telnyx connection",
                        dialer_provider: "telnyx"
                      });

                      // Try to connect with a timeout
                      await new Promise((resolve, reject) => {
                        const timeout = setTimeout(() => {
                          // Check if we're connected
                          if (this.state.connectionStatus === "connected") {
                            resolve();
                            return;
                          }
                          // Clean up listeners before timeout rejection
                          this._device.off("telnyx.socket.open", openHandler);
                          this._device.off("telnyx.socket.error", errorHandler);
                          reject(new Error("Connection timeout"));
                        }, 10000); // 10 second timeout for connection

                        // Check if already connected
                        if (this.state.connectionStatus === "connected") {
                          clearTimeout(timeout);
                          resolve();
                          return;
                        }

                        // Define handlers so we can remove them later
                        const openHandler = () => {
                          clearTimeout(timeout);
                          // Remove both listeners on success
                          this._device.off("telnyx.socket.open", openHandler);
                          this._device.off("telnyx.socket.error", errorHandler);
                          // Wait a bit for the main socket.open handler to update state
                          setTimeout(() => resolve(), 100);
                        };

                        const errorHandler = error => {
                          clearTimeout(timeout);
                          // Remove both listeners on error
                          this._device.off("telnyx.socket.open", openHandler);
                          this._device.off("telnyx.socket.error", errorHandler);
                          reject(error);
                        };

                        // Add the event listeners
                        this._device.on("telnyx.socket.open", openHandler);
                        this._device.on("telnyx.socket.error", errorHandler);

                        this._device.connect();
                      });

                      // Success
                      this.props.phoneCall({
                        token: this.props.token,
                        type: "log_info",
                        message: "Telnyx connection established successfully",
                        dialer_provider: "telnyx"
                      });

                      if (onSuccess) {
                        onSuccess(true);
                      }
                    } catch (error) {
                      console.error("Connection attempt failed:", error);
                      this.props.phoneCall({
                        token: this.props.token,
                        type: "log_error",
                        error_message: `Connection failed: ${error.message}`,
                        dialer_provider: "telnyx"
                      });

                      // Connection failed - only call onSuccess with false if not due to component unmounting
                      if (onSuccess && this.state.isMounted) {
                        onSuccess(false);
                      }
                    }
                  };

                  // Execute connection
                  connectToTelnyx();
                }
              }
            );
          } else {
            // Handle case where results are invalid
            console.error("Invalid Telnyx credentials received");

            if (onError) {
              onError("Invalid credentials received");
            }

            this.props.showErrorMessage(
              "Invalid phone service credentials. Please contact support.",
              "Configuration Error"
            );

            // If reconnecting, schedule retry
            if (this.state.isReconnecting) {
              this.scheduleReconnectionRetry();
            }
          }
        }
      });
    } else {
      if (onSuccess) {
        onSuccess();
      }
    }
  }

  getActiveIncomingPhoneCall() {
    this.props.getPhoneCall({
      token: this.props.token,
      type: "get_active_incoming_call",
      onLoading: () => {
        this.setState({
          loading_active_call: true
        });
      },
      onError: () => {
        this.setState(
          {
            loading_active_call: false
          },
          () => { }
        );
      },
      onSuccess: results => {
        const phone_call = results?.phone_call;

        if (phone_call && phone_call.answered) {
          this.setState(
            {
              loading_active_call: false,
              loading_call: false
            },
            () => {
              this.props.setActiveDialerContact({
                contact: {
                  ...phone_call?.contact,
                  selected_phone: {
                    number: phone_call?.to_phone_number
                  },
                  associated_lead: phone_call?.property,
                  one_off_call: true,
                  incoming_call: true,
                  answered_on_another_device: true
                },
                current_conversation: phone_call?.transcription,
                active_call: {
                  call_id: phone_call.call_id,
                  call_status: "connected"
                }
              });

              this.props.setDialerConversation({
                call_id: phone_call?.call_id,
                current_conversation: phone_call?.transcription,
                call_results: phone_call?.call_results,
                call_note: phone_call?.note
              });
            }
          );
        } else {
          this.setState(
            {
              loading_active_call: false
            },
            () => { }
          );
        }
      }
    });
  }

  createCallEvents(call) {
    call.on("disconnect", () => {
      console.log("disconnect");
      this.clearIncomingCall();
    });
    call.on("cancel", () => {
      console.log("cancel");

      this.clearIncomingCall();
    });

    call.on("error", error => {
      console.log("error");

      this.props.showErrorMessage(
        "There was an error with our phone provider. Please try again later",
        "Error"
      );

      this.props.phoneCall({
        token: this.props.token,
        type: "log_error",
        error_message: error,
        to_phone_number: this.props.active_call?.to_phone_number,
        from_phone_number: this.props.active_call?.from_phone_number,
        dialer_provider: "telnyx"
      });

      this.clearIncomingCall();
    });

    call.on("reject", () => {
      this.rejectIncomingCall();
    });
  }

  getIncomingCall(incoming_call_id, call) {
    if (!this.state.loading_call) {
      this.props.getPhoneCall({
        token: this.props.token,
        type: "get_phone_call",
        call_id: incoming_call_id,
        onLoading: () => {
          this.setState({
            loading_call: true
          });
        },
        onError: () => {
          this.setState({
            loading_call: false
          });
        },
        onSuccess: results => {
          const phone_call = results?.phone_call;
          if (phone_call) {
            if (!this.props.active_dialer_contact) {
              this.setState(
                {
                  loading_call: false,
                  incoming_call_id: incoming_call_id,
                  incoming_call_info: results?.phone_call,
                  incoming_call: call
                },
                () => {
                  this.props.resetActiveCall();

                  this.props.setActiveDialerContact({
                    contact: {
                      ...results?.phone_call?.contact,
                      selected_phone: {
                        number: phone_call?.to_phone_number
                      },
                      associated_lead: results?.phone_call?.property,
                      one_off_call: true,
                      incoming_call: true
                    },
                    active_call: {
                      call_id: phone_call.call_id,
                      call_status: "connected"
                    }
                  });
                }
              );
            } else {
              this.setState({
                loading_call: false,
                incoming_call_id: incoming_call_id,
                incoming_call_info: results?.phone_call,
                incoming_call: call
              });
            }
          }
        }
      });
    }
  }

  rejectIncomingCall() {
    this.props.setRejectedCall(this.state.incoming_call_id);

    this.setState({
      incoming_call_id: null,
      incoming_call: null,
      incoming_call_info: null
    });
  }
  clearIncomingCall() {
    this.setState({
      incoming_call_id: null,
      incoming_call: null,
      incoming_call_info: null
    });
  }

  render() {
    if (this._device) {
      return (
        <>
          {this.props.active_dialer_contact ? (
            <>
              <TelnyxDialerInner
                telnyx_client={this._device}
                clearIncomingCall={this.clearIncomingCall}
                rejectIncomingCall={this.rejectIncomingCall}
                active_dialer_contact={this.props.active_dialer_contact}
                onboarding={this.props.onboarding}
                incoming_call={this.state.incoming_call}
                incoming_call_info={this.state.incoming_call_info}
                call_id={this.state.incoming_call_id}
                refreshTelnyxToken={this.refreshTelnyxToken}
                hide_purchase_upsell={this.state.hide_purchase_upsell}
                hidePurchaseUpsell={this.hidePurchaseUpsell}
                mute={this.state.mute}
                muteCall={this.muteCall}
                localStream={this._localstream}
                resetTelnyxDevice={this.resetTelnyxDevice}
                connectionStatus={this.state.connectionStatus}
                checkConnectionAndReconnect={this.checkConnectionAndReconnect}
                handleCallFailure={this.handleCallFailure}
              />
            </>
          ) : null}
        </>
      );
    }

    return null;
  }
}

const mapStateToProps = ({ auth, settings, dialer }) => {
  const { token, user } = auth;
  const { dark_mode, colors } = settings;
  const { active_dialer_contact, active_call } = dialer;
  return {
    token,
    user,
    dark_mode,
    colors,
    active_dialer_contact,
    active_call
  };
};

export default connect(mapStateToProps, {
  getTelnyxToken,
  getPhoneCall,
  setActiveDialerContact,
  resetActiveCall,
  updateActiveCall,
  setDialerConversation,
  setRejectedCall,
  showErrorMessage,
  phoneCall,
  callControl
})(TelnyxDeviceHandler);
